import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import { archetypeOnlyOptions } from "building-types/options/optionDefinitions";
import type { OptionPrices } from "building-types/options/optionPricing";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Multimap } from "common/data/multimap";
import type { Table } from "common/data/table";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import type { ActiveOption, ActiveOptions } from "./activeOptions";
import { sortOptionSetIdsByBuilderOptionCode } from "./convertOptionSet";
import { getOptionPrices } from "./intersecting/intersectingOptionSetPrice";
import type { ActiveIntersectionEffect } from "./intersecting/intersectingOptionSetShared";
import { buildActiveOptions, calculateActiveIntersectionEffects } from "./intersecting/intersectingOptionSetTools";
import { isUninterestingBaseOption } from "./optionSetBaseUtils";
import { optionPrice } from "./optionSetTools";

export interface OptionsState {
  choices: OptionChoices;
  active: ActiveOptions;
  modifyingIntersections: {
    rendered: boolean;
    effects: Multimap<ActiveIntersectionEffect>;
  };
  generateEffects: ActiveIntersectionEffect[];
}

/**
 * Evaluates rules and prices against all choices. When activeOptionSetId is supplied, only
 * constructs that set's active display data; intersection effects and choices remain complete.
 */
export function calculateOptionsState(
  definitions: OptionDefinitions,
  optionChoices: OptionChoices,
  withIntersectionModificationsRendered: boolean,
  activeOptionSetId?: string,
): OptionsState {
  const intersections = calculateActiveIntersectionEffects(optionChoices, definitions);
  const prices = getOptionPrices(definitions, optionChoices, intersections, withIntersectionModificationsRendered);
  let activePrices: OptionPrices = prices;
  if (activeOptionSetId !== undefined) {
    activePrices = Object.hasOwn(prices, activeOptionSetId) ? { [activeOptionSetId]: prices[activeOptionSetId] } : {};
  }
  const active = buildActiveOptions(
    definitions,
    activePrices,
    intersections.modify,
    withIntersectionModificationsRendered,
  );

  return {
    choices: optionChoices,
    active,
    modifyingIntersections: { effects: intersections.modify, rendered: withIntersectionModificationsRendered },
    generateEffects: intersections.generate,
  };
}

function exportedIntersectingOptions(active: Multimap<ActiveIntersectionEffect>): ActiveOptions {
  return Object.fromEntries(
    Object.values(active)
      .flat()
      .map((m) => [
        m.option.parentId,
        {
          ...m.option,
          price: optionPrice(m.option),
        },
      ]),
  );
}

export function calculateExportedOptions(optionSets: Table<DocOptionSet>, optionChoices: OptionChoices): ActiveOptions {
  const state = calculateOptionsState(archetypeOnlyOptions(optionSets), optionChoices, false);
  return {
    ...state.active,
    ...exportedIntersectingOptions(state.modifyingIntersections.effects),
  };
}

export function getSortedActiveOptions(definitions: OptionDefinitions, active: ActiveOptions): ActiveOption[] {
  return sortOptionSetIdsByBuilderOptionCode(active, definitions)
    .map((optionSetId) => active[optionSetId])
    .filter((option) => {
      const optionSet = definitions.optionSets[option.parentId];
      // An opt-in (default-off) boolean toggle left off is not an included feature worth listing —
      // hide it from every option summary. Default-on toggles are always shown (kept or removed).
      if (isUninterestingBaseOption(optionSet, option.id)) {
        return false;
      }
      // We only want to show priced options in the operations category
      if (optionSet.salesCategory === SalesCategory.OPERATIONS) {
        return Boolean(option.price);
      }
      return true;
    });
}

export type DisconnectedOptionsTable = Table<Table<Formula>>;
